import { useEffect, useRef, useState } from "react";
import {
  Link,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  type InvoiceDetailDto,
  type InvoiceUpdateInput,
  vnDate,
} from "@hisense/shared";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import {
  useCreateInvoiceFromPi,
  useInvoice,
  useProformaInvoice,
  useUpdateInvoice,
} from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { InvoiceSourcePicker } from "@/components/invoices/InvoiceSourcePicker";
import { InvoiceLineAllocator } from "@/components/invoices/InvoiceLineAllocator";
import { InvoiceDocumentView } from "@/components/invoices/InvoiceDocumentView";
import { ApiClientError, getLocalizedError } from "@/lib/api";
const dateInput = (value: number | null) =>
  value ? vnDate(value) : "";
export function InvoiceEditorPage() {
  const { id } = useParams();
  const location = useLocation();
  const result = useInvoice(id);
  const { t } = useTranslation();
  if (id && result.isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (id && (result.error || !result.data))
    return (
      <PageBody>
        <ErrorState
          message={t("invoices.errorLoad")}
          onRetry={() => void result.refetch()}
        />
      </PageBody>
    );
  return (
    <InvoiceEditor
      key={
        id ??
        `new:${new URLSearchParams(location.search).get("sourcePi") ?? ""}`
      }
      invoice={result.data}
    />
  );
}
function InvoiceEditor({ invoice }: { invoice?: InvoiceDetailDto }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const initialSource = params.get("sourcePi") ?? "";
  params.delete("sourcePi");
  params.delete("archived");
  const context = params.size ? `?${params}` : "";
  const original = useRef(invoice);
  const [f, setF] = useState({
    piId: invoice?.piId ?? initialSource,
    date: dateInput(invoice?.date ?? Date.now()),
    shipTo: invoice?.shipTo ?? "",
    customerPoNo: invoice?.customerPoNo ?? "",
  });
  const initial = useRef(f);
  const [picker, setPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [archivedId, setArchivedId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  /**
   * Người dùng vừa chọn lại PI trong hộp chọn — KỂ CẢ đúng PI đang gắn.
   *
   * Không có cờ này thì chọn lại chính PI cũ là không có gì thay đổi, giao
   * diện coi như không cần làm gì và giá mới trên PI không bao giờ được kéo
   * sang hóa đơn — đúng việc khách cần làm.
   */
  const [repull, setRepull] = useState(false);
  const sourceChanged = !invoice || f.piId !== invoice.piId || repull;
  const pi = useProformaInvoice(sourceChanged ? f.piId : undefined);
  const loadedSource = useRef<string | null>(null);
  const create = useCreateInvoiceFromPi();
  /**
   * Số lượng xuất lần này theo từng dòng PI. Rỗng nghĩa là "lấy hết phần còn
   * lại" — bảng tự điền sẵn, nên lập trọn một lần vẫn chỉ là bấm một cái.
   */
  const [allocation, setAllocation] = useState<Record<string, number>>({});
  /**
   * Khóa chống bấm hai lần. Từ 15/09/2026 máy chủ không còn ngắn mạch "PI này
   * đã có hóa đơn" nữa, nên hai lần bấm nhanh sẽ ra HAI hóa đơn thật. Gửi lại
   * cùng khóa thì máy chủ trả về đúng bản đã tạo.
   */
  const requestId = useRef(crypto.randomUUID());
  const update = useUpdateInvoice(invoice?.id ?? "new");
  const lock = useRef(false);
  const allowNavigation = useRef(false);
  const busy = create.isPending || update.isPending;
  const dirty =
    JSON.stringify(f) !== JSON.stringify(initial.current) ||
    Boolean(!invoice && f.piId);
  const canEdit = Boolean(
    user &&
      can(invoice ? "records:update" : "records:create"),
  );
  const locked = Boolean(invoice?.deliveryNotes?.length);
  const blocker = useBlocker(
    () => !allowNavigation.current && (dirty || lock.current),
  );
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (!allowNavigation.current && (dirty || lock.current)) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [dirty]);
  useEffect(() => {
    if (
      !sourceChanged ||
      !pi.data ||
      pi.data.id !== f.piId ||
      loadedSource.current === f.piId
    )
      return;
    loadedSource.current = f.piId;
    setF((prev) => ({
      ...prev,
      shipTo: pi.data!.shipTo ?? "",
      customerPoNo: pi.data!.customerPoNo ?? "",
    }));
  }, [sourceChanged, pi.data, f.piId]);
  usePageCrumbs(
    invoice
      ? [
          { label: invoice.invNo, to: `/invoices/${invoice.id}${context}` },
          { label: t("common.edit") },
        ]
      : [{ label: t("invoices.newInvoice") }],
    `/invoices${context}`,
  );
  const proposed = sourceChanged ? pi.data : invoice;
  const save = async () => {
    if (!canEdit || lock.current || !f.piId || !proposed || !f.date || locked)
      return;
    if (sourceChanged && (pi.isFetching || loadedSource.current !== f.piId))
      return;
    lock.current = true;
    setError(null);
    setArchivedId(null);
    try {
      if (invoice) {
        const patch: InvoiceUpdateInput = {
          expectedUpdatedAt: original.current?.updatedAt,
        };
        if (sourceChanged) {
          patch.piId = f.piId;
          patch.expectedPiUpdatedAt = pi.data?.updatedAt;
        }
        if (f.date !== initial.current.date)
          patch.date = new Date(f.date + "T00:00:00Z").getTime();
        if (sourceChanged || f.shipTo !== initial.current.shipTo)
          patch.shipTo = f.shipTo;
        if (sourceChanged || f.customerPoNo !== initial.current.customerPoNo)
          patch.customerPoNo = f.customerPoNo;
        await update.mutateAsync(patch);
        allowNavigation.current = true;
        navigate(`/invoices/${invoice.id}${context}`);
      } else {
        const result = await create.mutateAsync({
          piId: f.piId,
          expectedPiUpdatedAt: pi.data?.updatedAt,
          requestId: requestId.current,
          // Không đụng vào bảng thì không gửi `lines`: máy chủ tự lấy toàn bộ
          // phần còn xuất được, đúng hành vi trước 15/09/2026.
          ...(Object.keys(allocation).length
            ? { lines: Object.entries(allocation).map(([piItemId, quantity]) => ({ piItemId, quantity })) }
            : {}),
        });
        allowNavigation.current = true;
        navigate(`/invoices/${result.data.id}${context}`);
      }
    } catch (err) {
      if (err instanceof ApiClientError && err.code === "invoice_archived")
        setArchivedId(err.fields?.invoiceId ?? null);
      setError(getLocalizedError(err, t, "invoices.errorSave"));
      setConfirm(false);
    } finally {
      lock.current = false;
    }
  };
  return (
    <PageBody>
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
      <ConfirmDialog
        open={confirm}
        tone="warn"
        title={t("invoiceWorkspace.replaceSource")}
        description={t("invoiceWorkspace.replaceHint")}
        confirmLabel={t("common.save")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => setConfirm(false)}
        onConfirm={() => void save()}
      />
      <h1 className="text-xl font-semibold">
        {t(invoice ? "invoices.editInvoice" : "invoices.newInvoice")}
        {invoice && <span className="ml-2 font-mono">{invoice.invNo}</span>}
      </h1>
      <p className="mt-1 text-sm text-ink-soft">
        {t("invoiceWorkspace.editorHint")}
      </p>
      {!canEdit && (
        <p className="mt-3 text-sm text-warn">{t("errors.readOnly")}</p>
      )}
      {locked && (
        <p className="mt-3 text-sm text-warn">
          {t("invoiceWorkspace.invoice_delivery_locked")}
        </p>
      )}
      <fieldset
        disabled={!canEdit || busy || locked}
        className="min-w-0 space-y-3"
      >
        <Panel className="mt-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-ink-faint">{t("invoices.selectPi")}</p>
              <p className="mt-1 font-mono text-brand">
                {proposed?.piNo || t("invoiceWorkspace.chooseSource")}
              </p>
            </div>
            <Button variant="secondary" onClick={() => setPicker(true)}>
              {t("invoiceWorkspace.chooseSource")}
            </Button>
          </div>
          {/*
            Chỉ hiện khi đang LẬP MỚI. Sửa một hóa đơn đã lập là chuyện khác —
            số lượng trên đó đã phát ra ngoài, đổi ở đây sẽ là sửa công nợ sau
            lưng người đã nhận chứng từ.
          */}
          {!invoice && f.piId && (
            <InvoiceLineAllocator piId={f.piId} value={allocation} onChange={setAllocation} />
          )}
          {invoice && sourceChanged && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-warn">
                {t("invoiceWorkspace.replaceHint")}
              </p>
              <Button
                variant="ghost"
                onClick={() => {
                  setF(initial.current);
                  loadedSource.current = null;
                }}
              >
                {t("common.cancel")}
              </Button>
            </div>
          )}
        </Panel>
        <Panel>
          <PanelHeader title={t("invoiceWorkspace.issueFields")} />
          <div className="grid gap-3 p-4 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor="invoice-date">{t("common.date")}</FieldLabel>
              <Input
                id="invoice-date"
                type="date"
                required
                disabled={!invoice}
                value={f.date}
                onChange={(e) => setF({ ...f, date: e.target.value })}
              />
            </div>
            <div>
              <FieldLabel htmlFor="invoice-po">{t("pi.poNo")}</FieldLabel>
              <Input
                id="invoice-po"
                disabled={!invoice}
                value={f.customerPoNo}
                onChange={(e) => setF({ ...f, customerPoNo: e.target.value })}
                maxLength={120}
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel htmlFor="invoice-contact">
                {t("pi.billToContactPerson")}
              </FieldLabel>
              <textarea
                id="invoice-contact"
                disabled={!invoice}
                rows={4}
                value={f.shipTo}
                onChange={(e) => setF({ ...f, shipTo: e.target.value })}
                className="w-full rounded-sm border border-line bg-surface p-3 text-sm"
              />
            </div>
          </div>
        </Panel>
      </fieldset>
      {sourceChanged && pi.isLoading ? (
        <LoadingState />
      ) : sourceChanged && pi.error ? (
        <ErrorState
          message={t("pi.errorLoad")}
          onRetry={() => void pi.refetch()}
        />
      ) : (
        proposed && (
          <div className="mt-4">
            <p className="mb-2 text-xs text-ink-faint">
              {t("invoiceWorkspace.previewSource")}
            </p>
            <InvoiceDocumentView
              document={{
                ...proposed,
                shipTo: f.shipTo,
                customerPoNo: f.customerPoNo,
                date: invoice
                  ? new Date(f.date + "T00:00:00Z").getTime()
                  : proposed.date,
              }}
            />
          </div>
        )
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {error}
        </p>
      )}
      {archivedId && (
        <Link
          className="mt-2 inline-block text-sm text-brand underline"
          to={`/invoices/${archivedId}?archived=true`}
        >
          {t("invoiceWorkspace.viewArchived")}
        </Link>
      )}
      <div className="sticky bottom-0 mt-4 flex justify-end gap-2 border-t border-line bg-surface p-3">
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() =>
            navigate(
              invoice
                ? `/invoices/${invoice.id}${context}`
                : `/invoices${context}`,
            )
          }
        >
          {t("common.cancel")}
        </Button>
        <Button
          disabled={
            !canEdit ||
            busy ||
            locked ||
            !proposed ||
            !f.piId ||
            (sourceChanged &&
              (pi.isFetching || loadedSource.current !== f.piId)) ||
            Boolean(invoice && !dirty)
          }
          onClick={() =>
            invoice && sourceChanged ? setConfirm(true) : void save()
          }
        >
          {t(
            busy ? "common.saving" : invoice ? "common.save" : "common.create",
          )}
        </Button>
      </div>
      {picker && (
        <InvoiceSourcePicker
          keepPiId={invoice?.piId ?? undefined}
          onClose={() => setPicker(false)}
          onPick={(id) => {
            loadedSource.current = null;
            setRepull(true);
            setAllocation({});
            requestId.current = crypto.randomUUID();
            setF((prev) => ({
              ...prev,
              piId: id,
              shipTo: "",
              customerPoNo: "",
            }));
            setPicker(false);
            setError(null);
          }}
        />
      )}
    </PageBody>
  );
}
